<template>
  <modal @cancel="closeModal">
    <template v-slot:title>
      <p class="modal-card-title">Add Holiday</p>
    </template>

    <template v-slot:body>
      <fieldset>
        <div class="field">
          <n-text-input
            ref="nameInput"
            v-model="name"
            label="Name"
            id="name"
            @input="$v.name.$touch()"
            :error-message="getErrorMessage('name', 'Name')"
          />
        </div>

        <div class="form-item">
          <label>
            On
            <n-tooltip
              position="right"
              text="Previously added holidays now in the past will automatically be removed"
            />
          </label>
        </div>

        <ul>
          <transition-group name="fade">
            <holiday-date-input
              v-for="(d, index) in dates"
              :key="d.key"
              v-model="dates[index].date"
              @update:model-value="$v.dates.$touch()"
              @delete="deleteDate(index)"
              ref="dateInputs"
            />
          </transition-group>

          <li class="date-list-item add-item-wrapper">
            <n-button variant="ghost" icon="add" :disabled="updateInProgress" @click="addDate"> Add Date </n-button>
            <p class="help is-danger" v-if="!$v.dates.required">You must add at least one date</p>
          </li>
        </ul>
      </fieldset>
    </template>

    <template v-slot:buttons>
      <n-button-group>
        <n-button variant="primary" :disabled="$v.$invalid" :loading="updateInProgress" @click="update">
          Create Holiday
        </n-button>
        <n-button :disabled="updateInProgress" @click="closeModal">Cancel</n-button>
      </n-button-group>
    </template>
  </modal>
</template>

<script lang="ts" setup>
import Toast from '@/shared/services/toast';
import Modal from '@/shared/components/Modal.vue';
import { CREATE_HOLIDAY } from '@/store/holidays/actionTypes';
import { HolidayViewModel } from '@/features/bots/models/holidayViewModel';
import VuelidateUtil from '@/shared/util/vuelidateUtil';
import { useStore } from 'vuex';
import { nextTick, onMounted, ref } from 'vue';
import { required, maxLength } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import NTextInput from '@/shared/designsystem/components/input/text-input/text-input.vue';
import HolidayDateInput from './HolidayDateInput.vue';

const store = useStore();
const createHoliday = (request: HolidayViewModel): Promise<HolidayViewModel> => store.dispatch(CREATE_HOLIDAY, request);

const name = ref('');
const dates = ref<any[]>([{ date: new Date(), key: 'first' }]);
const updateInProgress = ref(false);

const nameInput = ref<InstanceType<typeof NTextInput> | null>(null);
const dateInputs = ref<InstanceType<typeof HolidayDateInput>[]>([]);

const emit = defineEmits<{
  (e: 'create', holiday: HolidayViewModel): void;
  (e: 'cancel'): void;
}>();

const validations = {
  name: {
    required,
    maxLength: maxLength(50),
    holidayNameValid: VuelidateUtil.objectName,
  },
  dates: {
    required,
  },
};

const $v = useVuelidate(validations, { name, dates });

onMounted(() => focusOnInput(nameInput.value));

function closeModal() {
  emit('cancel');
}

function addDate() {
  dates.value.push({ date: new Date(), key: Date.now() });
  nextTick(() => {
    if (dateInputs.value) {
      const lastDateInput = dateInputs.value[dateInputs.value.length - 1];
      focusOnInput(lastDateInput);
    }
  });
}

function deleteDate(index: number) {
  dates.value.splice(index, 1);
}

function getErrorMessage(prop: 'name' | 'dates', label: string): string | null {
  if (!VuelidateUtil.hasPropertyBeenTouched($v, prop)) {
    return null;
  }

  return VuelidateUtil.getErrorMessage($v, prop, label, (failedRule) => {
    if (prop === 'name' && VuelidateUtil.isValidationInvalid($v, prop, 'holidayNameValid')) {
      return `${label} must only contain letters, numbers, spaces, and the characters , - _ ( ) &`;
    }
    return null;
  });
}

async function update() {
  if (updateInProgress.value) {
    return;
  }

  updateInProgress.value = true;
  try {
    const request = createRequestModel();
    const createdHoliday = await createHoliday(request);
    Toast.showSuccess('Holiday successfully created');
    emit('create', createdHoliday);
  } catch (err: any) {
    Toast.showError(err.message);
  } finally {
    updateInProgress.value = false;
  }
}

function createRequestModel(): HolidayViewModel {
  return {
    name: name.value,
    dates: dates.value.map((d) => d.date),
  };
}

function focusOnInput(component?: InstanceType<typeof NTextInput> | InstanceType<typeof HolidayDateInput> | null) {
  if (component) {
    const inputElements = component.$el.getElementsByTagName('input');
    if (inputElements.length === 1) {
      inputElements[0].focus();
    }
  }
}
</script>

<style scoped lang="scss">
.date-list-item {
  padding: 0.25rem 0;
}

.date-item {
  display: flex;
}

.icon-container {
  display: flex;
  justify-content: flex-end;
  align-items: center;

  .trash-icon {
    width: 1.25em;
    height: 1.25em;
    fill: #c7cdd3;
    margin-left: 0.5em;
    cursor: pointer;

    &:hover {
      fill: #a4a6a9;
    }
  }
}
</style>
